import React, { useCallback, useEffect, useState } from 'react';
import { residentsAPI, type GuestPass, type Resident } from '../../utils/api';
import { useConfirm } from '../ConfirmDialog/ConfirmDialog';
import { formatPhone, maskPhoneInput, normalizePhone, PHONE_PLACEHOLDER } from '../../utils/phone';
import '../../pages/Area/Area.css';
import './ResidentsModal.css';

interface ResidentsModalProps {
  areaId: number;
  areaName: string;
  onClose: () => void;
}

const MAX_GUEST_LIMIT = 100;

/** Бот, через который жители выписывают гостевые пропуска. */
const GUESTS_BOT_URL = 'https://t.me/guests_flypix_bot';

/** Слово «бот» ссылкой на бота гостевых пропусков. */
const BotLink: React.FC = () => (
  <a className="residents__bot-link" href={GUESTS_BOT_URL} target="_blank" rel="noopener noreferrer">
    бот
  </a>
);

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface FormState {
  fullName: string;
  phone: string;
  guestLimit: string;
}

const EMPTY_FORM: FormState = { fullName: '', phone: '', guestLimit: '2' };

/**
 * Жители участка: по телефону житель регистрируется в боте и приглашает гостей
 * (гостевой пропуск открывает шлагбаум по номеру машины гостя).
 */
const ResidentsModal: React.FC<ResidentsModalProps> = ({ areaId, areaName, onClose }) => {
  const { confirm, dialog: confirmDialog } = useConfirm();

  const [residents, setResidents] = useState<Resident[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  /** null — добавление; иначе редактируем жителя с этим id. */
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [message, setMessage] = useState<React.ReactNode>(null);

  const [busyId, setBusyId] = useState<number | null>(null);
  /** Житель, у которого раскрыт список гостей. */
  const [openPassesId, setOpenPassesId] = useState<number | null>(null);
  const [passes, setPasses] = useState<GuestPass[]>([]);
  const [passesLoading, setPassesLoading] = useState(false);
  const [passesError, setPassesError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const res = await residentsAPI.list(areaId);
      setResidents(res.success ? res.residents : []);
    } catch (e: any) {
      setResidents([]);
      setLoadError(e?.response?.message || e?.message || 'Не удалось загрузить пользователей');
    } finally {
      setLoading(false);
    }
  }, [areaId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !confirmDialog) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, confirmDialog]);

  const phoneNormalized = normalizePhone(form.phone);
  // Пока номер набирается (меньше 10 цифр) — не ругаемся, кнопка просто неактивна
  const phoneDigits = form.phone.replace(/\D+/g, '').length;
  const phoneInvalid = phoneDigits >= 11 && phoneNormalized === null;
  const guestLimit = parseInt(form.guestLimit, 10);
  const guestLimitInvalid = Number.isNaN(guestLimit) || guestLimit < 0 || guestLimit > MAX_GUEST_LIMIT;
  const canSubmit = form.fullName.trim() !== '' && phoneNormalized !== null && !guestLimitInvalid;

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setFormError(null);
  };

  const startEdit = (resident: Resident) => {
    setEditingId(resident.id);
    setForm({
      fullName: resident.full_name,
      phone: formatPhone(resident.phone),
      guestLimit: String(resident.guest_limit),
    });
    setFormError(null);
    setMessage(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setMessage(null);
    if (!canSubmit || !phoneNormalized) return;

    const payload = {
      full_name: form.fullName.trim(),
      phone: phoneNormalized,
      guest_limit: guestLimit,
    };

    try {
      setSaving(true);
      if (editingId === null) {
        await residentsAPI.add(areaId, payload);
        setMessage(
          <>
            Пользователь {payload.full_name} добавлен — по номеру {formatPhone(payload.phone)} он войдёт в <BotLink />
          </>
        );
      } else {
        // Отправляем только изменённые поля
        const original = residents.find((r) => r.id === editingId);
        const changed: typeof payload | Partial<typeof payload> = {};
        if (!original || original.full_name !== payload.full_name) changed.full_name = payload.full_name;
        if (!original || normalizePhone(original.phone) !== payload.phone) changed.phone = payload.phone;
        if (!original || original.guest_limit !== payload.guest_limit) changed.guest_limit = payload.guest_limit;

        if (Object.keys(changed).length > 0) {
          await residentsAPI.update(areaId, editingId, changed);
        }
        setMessage('Изменения сохранены');
      }
      resetForm();
      await load();
    } catch (err: any) {
      setFormError(err?.response?.message || err?.message || 'Не удалось сохранить пользователя');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (resident: Resident) => {
    const ok = await confirm({
      title: 'Удалить пользователя',
      tone: 'danger',
      message: (
        <>
          Пользователь «{resident.full_name}» больше не сможет приглашать гостей.
          {resident.active_passes > 0
            ? ` Его активные гостевые пропуска (${resident.active_passes}) тоже удалятся — гости не проедут.`
            : ''}
        </>
      ),
      confirmText: 'Удалить',
    });
    if (!ok) return;

    setMessage(null);
    setFormError(null);
    try {
      setBusyId(resident.id);
      await residentsAPI.remove(areaId, resident.id);
      setResidents((prev) => prev.filter((r) => r.id !== resident.id));
      if (editingId === resident.id) resetForm();
      if (openPassesId === resident.id) setOpenPassesId(null);
      setMessage(`Пользователь ${resident.full_name} удалён`);
    } catch (err: any) {
      setFormError(err?.response?.message || err?.message || 'Не удалось удалить пользователя');
    } finally {
      setBusyId(null);
    }
  };

  const togglePasses = async (resident: Resident) => {
    if (openPassesId === resident.id) {
      setOpenPassesId(null);
      return;
    }
    setOpenPassesId(resident.id);
    setPasses([]);
    setPassesError(null);
    try {
      setPassesLoading(true);
      const res = await residentsAPI.guestPasses(areaId, resident.id);
      setPasses(res.success ? res.passes : []);
    } catch (err: any) {
      setPassesError(err?.response?.message || err?.message || 'Не удалось загрузить гостей');
    } finally {
      setPassesLoading(false);
    }
  };

  return (
    <div className="area-modal" role="dialog" aria-modal="true">
      {confirmDialog}
      <div className="area-modal__backdrop" onClick={onClose} />
      <div className="area-modal__dialog residents__dialog">
        <header className="area-modal__header">
          <h2 className="area-modal__title">Пользователи · {areaName}</h2>
          <span className="residents__count">Пользователей: {residents.length}</span>
          <button className="area-modal__close" type="button" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </header>

        <div className="residents__body">
          <form className="residents__form" onSubmit={handleSubmit}>
            <div className="residents__form-title">
              {editingId === null ? 'Добавить пользователя' : 'Изменить пользователя'}
            </div>
            <div className="residents__form-row">
              <label className="residents__field residents__field--wide">
                <span className="residents__label">ФИО</span>
                <input
                  type="text"
                  className="area-modal__input"
                  value={form.fullName}
                  onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
                  placeholder="Фамилия Имя Отчество"
                  maxLength={255}
                  disabled={saving}
                />
              </label>
              <label className="residents__field">
                <span className="residents__label">Телефон</span>
                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  className={'area-modal__input' + (phoneInvalid ? ' residents__input--error' : '')}
                  value={form.phone}
                  // Маска «+7 900 000-00-00»: можно печатать с 8 или +7, вставлять в любом формате
                  onChange={(e) => {
                    const next = e.target.value;
                    setForm((f) => ({ ...f, phone: maskPhoneInput(next, f.phone) }));
                  }}
                  placeholder={PHONE_PLACEHOLDER}
                  disabled={saving}
                  aria-invalid={phoneInvalid}
                />
              </label>
              <label className="residents__field residents__field--narrow">
                <span className="residents__label">Лимит гостей</span>
                <input
                  type="number"
                  min={0}
                  max={MAX_GUEST_LIMIT}
                  step={1}
                  className={'area-modal__input' + (guestLimitInvalid ? ' residents__input--error' : '')}
                  value={form.guestLimit}
                  onChange={(e) => setForm((f) => ({ ...f, guestLimit: e.target.value }))}
                  disabled={saving}
                />
              </label>
            </div>
            <p className="residents__hint">
              По этому телефону пользователь войдёт в <BotLink /> и сможет выписывать гостевые пропуска — одновременно
              не больше лимита гостей.
            </p>

            <div className="residents__form-actions">
              {editingId !== null && (
                <button
                  type="button"
                  className="area-modal__submit area-modal__submit--secondary"
                  onClick={resetForm}
                  disabled={saving}
                >
                  Отмена
                </button>
              )}
              <button type="submit" className="area-modal__submit" disabled={saving || !canSubmit}>
                {saving ? 'Сохраняем…' : editingId === null ? 'Добавить' : 'Сохранить'}
              </button>
            </div>

            {phoneInvalid && !formError && (
              <div className="residents__message residents__message--error">
                Некорректный телефон. Пример: +7 900 000-00-00
              </div>
            )}
            {formError && <div className="residents__message residents__message--error">{formError}</div>}
            {message && !formError && (
              <div className="residents__message residents__message--success">{message}</div>
            )}
          </form>

          <section className="residents__list">
            {loading ? (
              <div className="ui-loading">Загрузка пользователей…</div>
            ) : loadError ? (
              <div className="residents__message residents__message--error">{loadError}</div>
            ) : residents.length === 0 ? (
              <div className="ui-empty">
                <span className="ui-empty__title">Пока ни одного пользователя</span>
                <span>Добавьте пользователя — он сможет приглашать гостей через бот.</span>
              </div>
            ) : (
              <ul className="residents__items">
                {residents.map((resident) => {
                  const open = openPassesId === resident.id;
                  const busy = busyId === resident.id;
                  const full = resident.guest_limit > 0 && resident.active_passes >= resident.guest_limit;
                  return (
                    <li
                      key={resident.id}
                      className={'residents__item' + (editingId === resident.id ? ' residents__item--editing' : '')}
                    >
                      <div className="residents__row">
                        <div className="residents__who">
                          <span className="residents__name">{resident.full_name || '—'}</span>
                          <span className="residents__phone">{formatPhone(resident.phone)}</span>
                        </div>
                        <span
                          className={'residents__guests' + (full ? ' residents__guests--full' : '')}
                          title="Активных гостевых пропусков / лимит"
                        >
                          Гостей: {resident.active_passes} из {resident.guest_limit}
                        </span>
                        <div className="residents__actions">
                          <button
                            type="button"
                            className="ui-btn ui-btn--outline ui-btn--sm"
                            onClick={() => void togglePasses(resident)}
                            aria-expanded={open}
                          >
                            {open ? 'Скрыть гостей' : 'Гости'}
                          </button>
                          <button
                            type="button"
                            className="ui-btn ui-btn--outline ui-btn--sm"
                            onClick={() => startEdit(resident)}
                            disabled={busy || saving}
                          >
                            Изменить
                          </button>
                          <button
                            type="button"
                            className="ui-btn ui-btn--outline ui-btn--sm residents__delete"
                            onClick={() => void handleDelete(resident)}
                            disabled={busy}
                          >
                            {busy ? 'Удаляем…' : 'Удалить'}
                          </button>
                        </div>
                      </div>

                      {open && (
                        <div className="residents__passes">
                          {passesLoading ? (
                            <div className="residents__passes-empty">Загрузка гостей…</div>
                          ) : passesError ? (
                            <div className="residents__message residents__message--error">{passesError}</div>
                          ) : passes.length === 0 ? (
                            <div className="residents__passes-empty">Активных гостевых пропусков нет</div>
                          ) : (
                            <ul className="residents__pass-list">
                              {passes.map((pass, idx) => (
                                <li key={pass.id ?? `${pass.plate}-${idx}`} className="residents__pass">
                                  <span className="residents__plate">{pass.plate}</span>
                                  <span className="residents__pass-meta">
                                    действует до {formatDateTime(pass.valid_to)}
                                  </span>
                                  <span
                                    className={
                                      'ui-badge ' + (pass.used_at ? 'ui-badge--success' : 'ui-badge--outline')
                                    }
                                  >
                                    {pass.used_at ? `Проехал ${formatDateTime(pass.used_at)}` : 'Ещё не проезжал'}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>

        <footer className="area-modal__footer">
          <button type="button" className="area-modal__submit area-modal__submit--secondary" onClick={onClose}>
            Закрыть
          </button>
        </footer>
      </div>
    </div>
  );
};

export default ResidentsModal;
